import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { useSession } from "../auth";
import { useScheme } from "../theme";
import { useProductConfig } from "../productConfig";
import { mark } from "../performance";

/**
 * The live strip in the top bar. FLEET CCU — every game at once.
 *
 * The label says "across all games" and not "playing now", because the
 * per-game header says "playing now" and the two sat a hundred pixels apart
 * showing different numbers under identical wording: 31 up here, 21 down
 * there. Same styling, same words, no way to tell which was which.
 *
 * Polled at the same 30s the API caches at; polling faster only burns the
 * cache's own TTL.
 */
function LiveStrip(): ReactNode {
  const { data, error } = useQuery({
    queryKey: ["live"],
    queryFn: ({ signal }) => api.live(signal),
    refetchInterval: 30_000,
  });

  return (
    <div
      className="livestrip"
      title={
        error
          ? "Last received count; live update failed"
          : "Current players across all games"
      }
    >
      <span className="livestrip__value">
        {data ? Math.round(data.data.total) : "—"}
      </span>
      <span className="livestrip__label">
        {error ? "last known total" : "across all games"}
      </span>
    </div>
  );
}

/**
 * The scheme itself lives in `theme.tsx`, because Mantine has to be told about
 * it too and two components remembering it separately is how a page ends up
 * half dark.
 */
function ThemeToggle(): ReactNode {
  const { scheme, toggle } = useScheme();
  return (
    <button
      className="button"
      onClick={toggle}
      aria-label={`Switch to ${scheme === "dark" ? "light" : "dark"} mode`}
    >
      {scheme === "dark" ? "Light" : "Dark"}
    </button>
  );
}

export function Shell({ children }: { children: ReactNode }): ReactNode {
  const { identity, signOut } = useSession();
  const { config } = useProductConfig();

  useEffect(() => {
    requestAnimationFrame(() => mark("shell-paint"));
  }, []);

  return (
    <div className="shell">
      {/* Fourteen tab stops separate the top bar from the content; without a
          way past them, a keyboard user re-traverses the whole chrome on every
          navigation. */}
      <a className="skiplink" href="#content">
        Skip to content
      </a>
      <header className="topbar">
        <Link to="/" className="topbar__mark">
          <img src={config.product.icon} alt="" />
          <span className="wordmark">{config.product.name}</span>
        </Link>
        {identity ? <LiveStrip /> : null}
        <div className="topbar__spacer" />
        {identity ? (
          <span
            className="accountlabel"
            title={`Signed in as ${identity.email}`}
          >
            {identity.email}
            {identity.role === "admin" ? " · admin" : ""}
          </span>
        ) : null}
        <ThemeToggle />
        {identity ? (
          <button className="button" onClick={() => void signOut()}>
            Sign out
          </button>
        ) : null}
      </header>
      <main className="main" id="content">
        {children}
      </main>
    </div>
  );
}
